import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import axios from "axios";
import { API } from "@/lib/api";
import { toast } from "sonner";

/* Admin Graph Editor — protected via shared password (not consumer auth).
 * Operates against /api/admin/entities with X-Admin-Password header.
 * Allows adding/editing/deleting entities and their L1/Nodal/PNO email nodes. */

export default function Admin() {
    const [pw, setPw] = useState(sessionStorage.getItem("speakup_admin_pw") || "");
    const [authed, setAuthed] = useState(!!sessionStorage.getItem("speakup_admin_pw"));
    const [items, setItems] = useState([]);
    const [reqs, setReqs] = useState([]);
    const [editing, setEditing] = useState(null); // entity object

    const adminApi = axios.create({
        baseURL: API,
        headers: { "X-Admin-Password": pw, "Content-Type": "application/json" },
    });

    const load = async () => {
        try {
            const r = await adminApi.get("/admin/entities");
            setItems(r.data.items);
            setReqs(r.data.requests);
            setAuthed(true);
            sessionStorage.setItem("speakup_admin_pw", pw);
        } catch (e) {
            toast.error("Incorrect admin password");
            setAuthed(false);
        }
    };

    useEffect(() => {
        if (pw) load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const remove = async (id) => {
        if (!window.confirm("Delete this entity?")) return;
        await adminApi.delete(`/admin/entities/${id}`);
        load();
    };

    if (!authed) {
        return (
            <Layout hideHeader>
                <div className="max-w-md mx-auto mt-16 speakup-card p-6" data-testid="admin-login-card">
                    <h1 className="display text-[22px]">Admin — graph editor</h1>
                    <input
                        type="password"
                        value={pw}
                        onChange={(e) => setPw(e.target.value)}
                        placeholder="Admin password"
                        className="mt-4 w-full h-11 px-3 rounded-[10px] border border-[var(--speakup-border)]"
                        data-testid="admin-password-input"
                    />
                    <button
                        onClick={load}
                        data-testid="admin-login-btn"
                        className="mt-3 w-full h-11 rounded-[10px] text-white font-semibold"
                        style={{ background: "var(--speakup-red)" }}
                    >
                        Sign in
                    </button>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <h1 className="display text-[26px]">Escalation graph — admin</h1>
            <p className="text-[var(--speakup-text-muted)] mt-1">
                Add/edit institutions with their L1 (Customer Care), L2 (Nodal Officer), and L3 (Principal Nodal Officer) email addresses.
            </p>

            <div className="mt-5">
                <button
                    onClick={() => setEditing({ name: "", type: "bank", aliases: [], nodes: defaultNodes() })}
                    data-testid="admin-add-entity-btn"
                    className="h-11 px-4 rounded-[10px] text-white font-semibold"
                    style={{ background: "var(--speakup-red)" }}
                >
                    + Add entity
                </button>
            </div>

            <div className="mt-5 grid gap-3" data-testid="admin-entities-list">
                {items.map((e) => (
                    <div key={e.id} className="speakup-card p-4" data-testid={`admin-entity-${e.id}`}>
                        <div className="flex items-start justify-between">
                            <div>
                                <div className="font-semibold">
                                    {e.name} <span className="text-[var(--speakup-text-muted)] text-[12px] uppercase">{e.type}</span>
                                </div>
                                <div className="text-[12px] text-[var(--speakup-text-muted)]">
                                    Aliases: {(e.aliases || []).join(", ") || "—"}
                                </div>
                                <ul className="text-[13px] mt-2 grid gap-1">
                                    {[...(e.nodes || [])].sort((a, b) => a.level - b.level).map((n) => (
                                        <li key={n.id} className="tabular">
                                            L{n.level} → {n.address || n.portal_url || "(no address)"} · SLA {n.sla_days}d
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className="flex gap-2">
                                <button onClick={() => setEditing(e)} data-testid={`admin-edit-${e.id}`} className="text-sm underline">Edit</button>
                                <button onClick={() => remove(e.id)} data-testid={`admin-delete-${e.id}`} className="text-sm underline text-[var(--speakup-red)]">Delete</button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {reqs.length > 0 && (
                <div className="mt-7">
                    <div className="display text-[18px]">User requests</div>
                    <ul className="mt-2 grid gap-2">
                        {reqs.map((r) => (
                            <li key={r.id} className="speakup-card p-3 text-[14px]">
                                <span className="font-semibold">{r.name}</span> — {r.note || "(no note)"}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {editing && (
                <EntityEditor
                    entity={editing}
                    onClose={() => setEditing(null)}
                    onSave={async (body) => {
                        if (editing.id) await adminApi.put(`/admin/entities/${editing.id}`, body);
                        else await adminApi.post("/admin/entities", body);
                        setEditing(null);
                        await load();
                        toast.success("Saved");
                    }}
                />
            )}
        </Layout>
    );
}

function defaultNodes() {
    return [1, 2, 3].map((lvl) => ({
        level: lvl,
        channel_type: "email",
        address: "",
        portal_url: "",
        sla_days: 10,
        preferred_channel: lvl === 1,
        source_url: "",
        confidence: 0.85,
    }));
}

function EntityEditor({ entity, onClose, onSave }) {
    const [name, setName] = useState(entity.name);
    const [type, setType] = useState(entity.type);
    const [aliases, setAliases] = useState((entity.aliases || []).join(", "));
    const [nodes, setNodes] = useState(() => {
        const out = defaultNodes();
        (entity.nodes || []).forEach((n) => {
            const i = out.findIndex((x) => x.level === n.level);
            if (i >= 0) out[i] = { ...out[i], ...n };
        });
        return out;
    });

    const update = (lvl, k, v) =>
        setNodes(nodes.map((n) => (n.level === lvl ? { ...n, [k]: v } : n)));

    return (
        <div className="fixed inset-0 z-40 bg-black/50 flex items-end md:items-center justify-center p-3" data-testid="admin-editor-modal">
            <div className="speakup-card max-w-2xl w-full max-h-[90vh] overflow-auto p-6">
                <div className="display text-[20px]">{entity.id ? "Edit entity" : "Add entity"}</div>
                <div className="mt-4 grid md:grid-cols-2 gap-3">
                    <Field label="Name">
                        <input value={name} onChange={(e) => setName(e.target.value)} data-testid="admin-edit-name"
                               className="w-full h-11 px-3 rounded-[10px] border border-[var(--speakup-border)]" />
                    </Field>
                    <Field label="Type">
                        <select value={type} onChange={(e) => setType(e.target.value)} data-testid="admin-edit-type"
                                className="w-full h-11 px-3 rounded-[10px] border border-[var(--speakup-border)] bg-white">
                            {["bank", "nbfc", "card", "pso", "fintech"].map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                    </Field>
                </div>
                <Field label="Aliases (comma-separated)">
                    <input value={aliases} onChange={(e) => setAliases(e.target.value)} data-testid="admin-edit-aliases"
                           className="w-full h-11 px-3 rounded-[10px] border border-[var(--speakup-border)]" />
                </Field>

                <div className="mt-4 grid gap-3">
                    {nodes.map((n) => (
                        <div key={n.level} className="border border-[var(--speakup-border)] rounded-[10px] p-3">
                            <div className="font-semibold text-[14px]">L{n.level} · {{1: "Customer Care", 2: "Nodal Officer", 3: "Principal Nodal Officer"}[n.level]}</div>
                            <div className="mt-2 grid md:grid-cols-2 gap-3">
                                <input placeholder="Email address" value={n.address || ""} onChange={(e) => update(n.level, "address", e.target.value)}
                                       data-testid={`admin-edit-l${n.level}-address`}
                                       className="h-10 px-3 rounded-[8px] border border-[var(--speakup-border)] tabular" />
                                <input placeholder="Source URL" value={n.source_url || ""} onChange={(e) => update(n.level, "source_url", e.target.value)}
                                       className="h-10 px-3 rounded-[8px] border border-[var(--speakup-border)]" />
                                <input type="number" placeholder="SLA days" value={n.sla_days} onChange={(e) => update(n.level, "sla_days", Number(e.target.value))}
                                       className="h-10 px-3 rounded-[8px] border border-[var(--speakup-border)] tabular" />
                            </div>
                        </div>
                    ))}
                </div>

                <div className="mt-5 flex justify-end gap-2">
                    <button onClick={onClose} data-testid="admin-edit-cancel" className="h-11 px-4 rounded-[10px] border border-[var(--speakup-border)]">Cancel</button>
                    <button
                        onClick={() => onSave({
                            name,
                            type,
                            aliases: aliases.split(",").map((s) => s.trim()).filter(Boolean),
                            nodes: nodes.map((n) => ({
                                level: n.level,
                                channel_type: n.channel_type,
                                address: n.address || null,
                                portal_url: n.portal_url || null,
                                sla_days: n.sla_days,
                                preferred_channel: n.preferred_channel,
                                source_url: n.source_url || null,
                                confidence: n.confidence,
                            })),
                        })}
                        data-testid="admin-edit-save"
                        className="h-11 px-5 rounded-[10px] text-white font-semibold"
                        style={{ background: "var(--speakup-red)" }}
                    >
                        Save
                    </button>
                </div>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <div className="text-[13px] font-semibold text-[var(--speakup-text-strong)] mb-1.5">{label}</div>
            {children}
        </label>
    );
}
